import { useCallback, useEffect, useRef, useState } from 'react';
import toast from 'react-hot-toast';
import {
  fetchAddresses,
  createAddress,
  updateAddress,
  deleteAddress,
  setDefaultAddress,
} from '../../services/api/addressService';
import './SavedAddressPicker.scss';

const EMPTY_FORM = {
  label: '',
  address_line: '',
  city: '',
  state: '',
  postal_code: '',
  country: '',
  latitude: '',
  longitude: '',
  is_default: false,
};

const FALLBACK_CENTER = { lat: 31.5204, lng: 74.3587 };

const LABEL_ICONS = {
  home: '\u{1F3E0}',
  office: '\u{1F3E2}',
  work: '\u{1F3E2}',
  other: '\u{1F4CD}',
};
const LABEL_PRESETS = ['Home', 'Office', 'Other'];

function labelIcon(label) {
  const key = String(label || '').trim().toLowerCase();
  return LABEL_ICONS[key] || '\u{1F4CD}';
}

const getGoogleAddressParts = (result) => {
  const parts = { city: '', state: '', postcode: '', country: '' };
  (result?.address_components || []).forEach((c) => {
    const t = c.types || [];
    if (t.includes('locality') || t.includes('postal_town')) parts.city = c.long_name;
    if (t.includes('administrative_area_level_1')) parts.state = c.long_name;
    if (t.includes('postal_code')) parts.postcode = c.long_name;
    if (t.includes('country')) parts.country = c.long_name;
  });
  return parts;
};

const pickBestGeocodeResult = (results = []) => {
  for (const type of ['street_address', 'premise', 'subpremise', 'route', 'establishment']) {
    const match = results.find((r) => r.types?.includes(type));
    if (match) return match;
  }
  return results[0] || null;
};

function ensureGoogleMaps() {
  if (window.google?.maps?.Map && window.google?.maps?.Geocoder) {
    return Promise.resolve(window.google.maps);
  }
  const key = process.env.REACT_APP_GOOGLE_MAPS_API_KEY;
  if (!key) return Promise.reject(new Error('Missing Google Maps key'));

  if (!window.__sapGoogleMapsPromise) {
    window.__sapGoogleMapsPromise = new Promise((resolve, reject) => {
      const existing = document.querySelector('script[data-google-maps-loader="true"]');
      if (existing) {
        if (window.google?.maps?.Map) return resolve(window.google.maps);
        existing.addEventListener('load', () => resolve(window.google.maps), { once: true });
        existing.addEventListener('error', () => reject(new Error('Maps load failed')), { once: true });
        return;
      }
      const s = document.createElement('script');
      s.src = `https://maps.googleapis.com/maps/api/js?key=${key}&libraries=places`;
      s.async = true;
      s.defer = true;
      s.dataset.googleMapsLoader = 'true';
      s.onload = () => resolve(window.google.maps);
      s.onerror = () => reject(new Error('Maps load failed'));
      document.head.appendChild(s);
    });
  }
  return window.__sapGoogleMapsPromise;
}

export default function SavedAddressPicker({ token, onSelect, reloadRef }) {
  const [addresses, setAddresses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  const hasAutoSelectedRef = useRef(false);
  const [selectedId, setSelectedId] = useState(null);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const dropdownRef = useRef(null);

  // Google Maps + autocomplete refs
  const formMapElRef = useRef(null);
  const formMapRef = useRef(null);
  const formMarkerRef = useRef(null);
  const geocoderRef = useRef(null);
  const autocompleteServiceRef = useRef(null);
  const sessionTokenRef = useRef(null);
  const [suggestions, setSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [highlightIdx, setHighlightIdx] = useState(-1);
  const suggestionsRef = useRef(null);
  const addressInputRef = useRef(null);

  /* ── data loading ── */
  const load = useCallback(async (autoSelect = false) => {
    if (!token) return;
    setLoading(true);
    try {
      const raw = await fetchAddresses(token);
      const list = Array.isArray(raw) ? raw : [];
      setAddresses(list);

      if (autoSelect && !hasAutoSelectedRef.current && list.length > 0) {
        const defaultAddr = list.find((a) => a.is_default) || list[0];
        hasAutoSelectedRef.current = true;
        setSelectedId(defaultAddr.id);
        onSelect?.({
          address: defaultAddr.address_line,
          coords:
            defaultAddr.latitude != null && defaultAddr.longitude != null
              ? { latitude: Number(defaultAddr.latitude), longitude: Number(defaultAddr.longitude) }
              : null,
        });
      }
    } catch {
      /* silent */
    } finally {
      setLoading(false);
    }
  }, [token, onSelect]);

  useEffect(() => { load(true); }, [load]);
  useEffect(() => { if (reloadRef) reloadRef.current = () => load(false); }, [reloadRef, load]);

  /* ── select saved address → checkout ── */
  const handleSelect = useCallback(
    (addr) => {
      setSelectedId(addr.id);
      setDropdownOpen(false);
      onSelect?.({
        address: addr.address_line,
        coords:
          addr.latitude != null && addr.longitude != null
            ? { latitude: Number(addr.latitude), longitude: Number(addr.longitude) }
            : null,
      });
    },
    [onSelect],
  );

  // close dropdown on outside click
  useEffect(() => {
    if (!dropdownOpen) return;
    const handler = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [dropdownOpen]);

  /* ── form open / close ── */
  const openNew = () => {
    setEditingId(null);
    setForm(EMPTY_FORM);
    setShowForm(true);
  };

  const openEdit = (addr) => {
    setEditingId(addr.id);
    setForm({
      label: addr.label || '',
      address_line: addr.address_line || '',
      city: addr.city || '',
      state: addr.state || '',
      postal_code: addr.postal_code || '',
      country: addr.country || '',
      latitude: addr.latitude ?? '',
      longitude: addr.longitude ?? '',
      is_default: !!addr.is_default,
    });
    setShowForm(true);
  };

  const closeForm = () => {
    setShowForm(false);
    setEditingId(null);
    setForm(EMPTY_FORM);
    setSuggestions([]);
    setShowSuggestions(false);
  };

  const setField = (field, value) => setForm((prev) => ({ ...prev, [field]: value }));
  const handleChange = (field) => (e) => {
    const value = e.target.type === 'checkbox' ? e.target.checked : e.target.value;
    setField(field, value);
  };

  /* ── reverse-geocode helper (pin → address fields) ── */
  const reverseGeocode = useCallback(async (lat, lng) => {
    try {
      const maps = await ensureGoogleMaps();
      if (!geocoderRef.current) geocoderRef.current = new maps.Geocoder();
      const { results } = await new Promise((resolve, reject) => {
        geocoderRef.current.geocode({ location: { lat, lng } }, (r, status) =>
          status === 'OK' && r?.length ? resolve({ results: r }) : reject(new Error(status)),
        );
      });
      const best = pickBestGeocodeResult(results);
      const parts = getGoogleAddressParts(best);
      const formatted = best?.formatted_address || `${lat.toFixed(6)}, ${lng.toFixed(6)}`;

      setForm((prev) => ({
        ...prev,
        address_line: formatted,
        city: parts.city || prev.city,
        state: parts.state || prev.state,
        postal_code: parts.postcode || prev.postal_code,
        country: parts.country || prev.country,
        latitude: lat,
        longitude: lng,
      }));
    } catch {
      setForm((prev) => ({ ...prev, latitude: lat, longitude: lng }));
    }
  }, []);

  /* ── initialize mini-map when form opens ── */
  useEffect(() => {
    if (!showForm || !formMapElRef.current || formMapRef.current) return;
    let cancelled = false;

    ensureGoogleMaps()
      .then((maps) => {
        if (cancelled || !formMapElRef.current) return;

        const hasCoords = form.latitude !== '' && form.longitude !== '';
        const center = hasCoords
          ? { lat: Number(form.latitude), lng: Number(form.longitude) }
          : FALLBACK_CENTER;

        formMapRef.current = new maps.Map(formMapElRef.current, {
          center,
          zoom: hasCoords ? 16 : 12,
          mapTypeControl: false,
          streetViewControl: false,
          fullscreenControl: false,
        });

        formMarkerRef.current = new maps.Marker({
          position: center,
          map: formMapRef.current,
          draggable: true,
          title: 'Drag to set location',
        });

        formMapRef.current.addListener('click', (e) => {
          if (!e?.latLng) return;
          const lat = e.latLng.lat();
          const lng = e.latLng.lng();
          formMarkerRef.current?.setPosition({ lat, lng });
          reverseGeocode(lat, lng);
        });

        formMarkerRef.current.addListener('dragend', (e) => {
          if (!e?.latLng) return;
          const lat = e.latLng.lat();
          const lng = e.latLng.lng();
          reverseGeocode(lat, lng);
        });
      })
      .catch(() => { /* map not available */ });

    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showForm]);

  // Clean up map instance when form closes
  useEffect(() => {
    if (!showForm) {
      formMapRef.current = null;
      formMarkerRef.current = null;
    }
  }, [showForm]);

  // Sync marker position when editing an address with existing coords
  useEffect(() => {
    if (!showForm || !formMarkerRef.current || !formMapRef.current) return;
    if (form.latitude === '' || form.longitude === '') return;
    const pos = { lat: Number(form.latitude), lng: Number(form.longitude) };
    formMarkerRef.current.setPosition(pos);
  }, [showForm, form.latitude, form.longitude]);

  /* ── Google Places autocomplete on address input ── */
  const fetchSuggestions = useCallback(async (input) => {
    if (!input || input.length < 3) {
      setSuggestions([]);
      setShowSuggestions(false);
      return;
    }
    try {
      const maps = await ensureGoogleMaps();
      if (!autocompleteServiceRef.current) {
        autocompleteServiceRef.current = new maps.places.AutocompleteService();
      }
      if (!sessionTokenRef.current) {
        sessionTokenRef.current = new maps.places.AutocompleteSessionToken();
      }
      autocompleteServiceRef.current.getPlacePredictions(
        { input, sessionToken: sessionTokenRef.current },
        (predictions, status) => {
          if (status === 'OK' && predictions?.length) {
            setSuggestions(predictions);
            setShowSuggestions(true);
            setHighlightIdx(-1);
          } else {
            setSuggestions([]);
            setShowSuggestions(false);
          }
        },
      );
    } catch {
      setSuggestions([]);
    }
  }, []);

  const selectSuggestion = useCallback(async (prediction) => {
    setSuggestions([]);
    setShowSuggestions(false);

    try {
      const maps = await ensureGoogleMaps();
      if (!geocoderRef.current) geocoderRef.current = new maps.Geocoder();
      sessionTokenRef.current = new maps.places.AutocompleteSessionToken();

      const { results } = await new Promise((resolve, reject) => {
        geocoderRef.current.geocode({ placeId: prediction.place_id }, (r, status) =>
          status === 'OK' && r?.length ? resolve({ results: r }) : reject(new Error(status)),
        );
      });

      const best = results[0];
      if (!best) return;

      const parts = getGoogleAddressParts(best);
      const loc = best.geometry?.location;
      const lat = typeof loc?.lat === 'function' ? loc.lat() : null;
      const lng = typeof loc?.lng === 'function' ? loc.lng() : null;

      setForm((prev) => ({
        ...prev,
        address_line: best.formatted_address || prediction.description,
        city: parts.city || prev.city,
        state: parts.state || prev.state,
        postal_code: parts.postcode || prev.postal_code,
        country: parts.country || prev.country,
        latitude: lat ?? prev.latitude,
        longitude: lng ?? prev.longitude,
      }));

      if (lat != null && lng != null && formMapRef.current && formMarkerRef.current) {
        const pos = { lat, lng };
        formMapRef.current.panTo(pos);
        formMapRef.current.setZoom(16);
        formMarkerRef.current.setPosition(pos);
      }
    } catch {
      setForm((prev) => ({ ...prev, address_line: prediction.description }));
    }
  }, []);

  const handleAddressKeyDown = (e) => {
    if (!showSuggestions || !suggestions.length) return;
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setHighlightIdx((i) => Math.min(i + 1, suggestions.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setHighlightIdx((i) => Math.max(i - 1, 0));
    } else if (e.key === 'Enter' && highlightIdx >= 0) {
      e.preventDefault();
      selectSuggestion(suggestions[highlightIdx]);
    } else if (e.key === 'Escape') {
      setShowSuggestions(false);
    }
  };

  // Close suggestions when clicking outside
  useEffect(() => {
    if (!showSuggestions) return;
    const handler = (e) => {
      if (
        suggestionsRef.current && !suggestionsRef.current.contains(e.target) &&
        addressInputRef.current && !addressInputRef.current.contains(e.target)
      ) {
        setShowSuggestions(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [showSuggestions]);

  /* ── save / delete handlers ── */
  const handleSave = async () => {
    if (!String(form.address_line || '').trim()) {
      toast.error('Address line is required');
      return;
    }

    const payload = {
      ...form,
      latitude: form.latitude !== '' ? Number(form.latitude) : null,
      longitude: form.longitude !== '' ? Number(form.longitude) : null,
    };

    setSaving(true);
    try {
      if (editingId) {
        await updateAddress(editingId, payload, token);
        toast.success('Address updated');
      } else {
        await createAddress(payload, token);
        toast.success('Address saved');
      }
      closeForm();
      await load();
    } catch (e) {
      toast.error(e?.message || 'Could not save address');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this address?')) return;
    try {
      await deleteAddress(id, token);
      toast.success('Address deleted');
      await load();
    } catch (e) {
      toast.error(e?.message || 'Could not delete');
    }
  };

  const handleSetDefault = async (id) => {
    try {
      await setDefaultAddress(id, token);
      await load();
    } catch (e) {
      toast.error(e?.message || 'Could not set default');
    }
  };

  const selectedAddr = addresses.find((a) => a.id === selectedId);

  if (loading) {
    return <div className="sap-loading">Loading saved addresses...</div>;
  }

  return (
    <div className="sap">
      {/* ── Dropdown selector ── */}
      {addresses.length > 0 && (
        <div className="sap-dropdown" ref={dropdownRef}>
          <button
            type="button"
            className="sap-dropdown__trigger"
            onClick={() => setDropdownOpen((v) => !v)}
          >
            <span className="sap-dropdown__icon">{labelIcon(selectedAddr?.label)}</span>
            <span className="sap-dropdown__text">
              <span className="sap-dropdown__label">
                {selectedAddr?.label || 'Address'}
                {selectedAddr?.is_default && <span className="sap-badge">Default</span>}
              </span>
              <span className="sap-dropdown__line">
                {selectedAddr?.address_line || 'Select an address'}
              </span>
            </span>
            <span className={`sap-dropdown__arrow ${dropdownOpen ? 'sap-dropdown__arrow--open' : ''}`}>&#9662;</span>
          </button>

          {dropdownOpen && (
            <div className="sap-dropdown__menu">
              {addresses.map((addr) => (
                <div
                  key={addr.id}
                  className={`sap-dropdown__item ${addr.id === selectedId ? 'sap-dropdown__item--active' : ''}`}
                >
                  <button
                    type="button"
                    className="sap-dropdown__item-body"
                    onClick={() => handleSelect(addr)}
                  >
                    <span className="sap-dropdown__item-icon">{labelIcon(addr.label)}</span>
                    <span className="sap-dropdown__item-text">
                      <span className="sap-dropdown__item-label">
                        {addr.label || 'Address'}
                        {addr.is_default && <span className="sap-badge">Default</span>}
                      </span>
                      <span className="sap-dropdown__item-line">{addr.address_line}</span>
                      {(addr.city || addr.state) && (
                        <span className="sap-dropdown__item-sub">
                          {[addr.city, addr.state, addr.postal_code].filter(Boolean).join(', ')}
                        </span>
                      )}
                    </span>
                  </button>
                  <div className="sap-dropdown__item-actions">
                    {!addr.is_default && (
                      <button type="button" className="sap-btn sap-btn--tiny" onClick={() => handleSetDefault(addr.id)}>
                        Set default
                      </button>
                    )}
                    <button type="button" className="sap-btn sap-btn--tiny" onClick={() => { setDropdownOpen(false); openEdit(addr); }}>
                      Edit
                    </button>
                    <button type="button" className="sap-btn sap-btn--tiny sap-btn--danger" onClick={() => handleDelete(addr.id)}>
                      Delete
                    </button>
                  </div>
                </div>
              ))}

              <button
                type="button"
                className="sap-dropdown__add"
                onClick={() => { setDropdownOpen(false); openNew(); }}
              >
                + Add new address
              </button>
            </div>
          )}
        </div>
      )}

      {addresses.length === 0 && !showForm && (
        <button type="button" className="sap-btn sap-btn--add" onClick={openNew}>
          + Add new address
        </button>
      )}

      {showForm && (
        <div className="sap-form">
          <h4 className="sap-form__title">{editingId ? 'Edit Address' : 'New Address'}</h4>

          <div className="sap-form__map-wrap">
            <div ref={formMapElRef} className="sap-form__map" />
          </div>
          <p className="sap-form__map-hint">Click on the map or drag the pin to set location</p>

          <div className="sap-form__row">
            <div className="sap-label-presets">
              {LABEL_PRESETS.map((preset) => (
                <button
                  key={preset}
                  type="button"
                  className={`sap-label-chip ${form.label === preset ? 'sap-label-chip--active' : ''}`}
                  onClick={() => setField('label', form.label === preset ? '' : preset)}
                >
                  <span className="sap-label-chip__icon">{labelIcon(preset)}</span>
                  {preset}
                </button>
              ))}
              <input
                className="sap-input sap-label-custom"
                placeholder="Custom label..."
                value={LABEL_PRESETS.includes(form.label) ? '' : form.label}
                onChange={(e) => setField('label', e.target.value)}
                onFocus={() => { if (LABEL_PRESETS.includes(form.label)) setField('label', ''); }}
              />
            </div>
          </div>

          <div className="sap-form__row sap-autocomplete-wrap">
            <input
              ref={addressInputRef}
              className="sap-input"
              placeholder="Search or type full address *"
              value={form.address_line}
              onChange={(e) => {
                setField('address_line', e.target.value);
                fetchSuggestions(e.target.value);
              }}
              onFocus={() => { if (suggestions.length) setShowSuggestions(true); }}
              onKeyDown={handleAddressKeyDown}
              autoComplete="off"
            />
            {showSuggestions && suggestions.length > 0 && (
              <ul className="sap-suggestions" ref={suggestionsRef}>
                {suggestions.map((s, i) => (
                  <li
                    key={s.place_id}
                    className={`sap-suggestions__item ${i === highlightIdx ? 'sap-suggestions__item--hl' : ''}`}
                    onMouseDown={() => selectSuggestion(s)}
                    onMouseEnter={() => setHighlightIdx(i)}
                  >
                    <span className="sap-suggestions__main">{s.structured_formatting?.main_text || s.description}</span>
                    {s.structured_formatting?.secondary_text && (
                      <span className="sap-suggestions__secondary">{s.structured_formatting.secondary_text}</span>
                    )}
                  </li>
                ))}
              </ul>
            )}
          </div>

          <div className="sap-form__grid">
            <input className="sap-input" placeholder="City" value={form.city} onChange={handleChange('city')} />
            <input className="sap-input" placeholder="State" value={form.state} onChange={handleChange('state')} />
          </div>

          <div className="sap-form__grid">
            <input className="sap-input" placeholder="Postal code" value={form.postal_code} onChange={handleChange('postal_code')} />
            <input className="sap-input" placeholder="Country" value={form.country} onChange={handleChange('country')} />
          </div>

          {(form.latitude !== '' || form.longitude !== '') && (
            <div className="sap-form__coords">
              Lat {Number(form.latitude).toFixed(6)} · Long {Number(form.longitude).toFixed(6)}
            </div>
          )}

          <label className="sap-checkbox">
            <input type="checkbox" checked={form.is_default} onChange={handleChange('is_default')} />
            <span>Set as default address</span>
          </label>

          <div className="sap-form__actions">
            <button type="button" className="sap-btn sap-btn--primary" onClick={handleSave} disabled={saving}>
              {saving ? 'Saving...' : editingId ? 'Update' : 'Save'}
            </button>
            <button type="button" className="sap-btn" onClick={closeForm} disabled={saving}>
              Cancel
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
